// @ts-nocheck
// ConversationThread — ONE conversation with a person, every channel interleaved,
// the composer at the bottom. The Inbox renders it in its thread pane and the
// customer window renders it as the Communications tab, so a call looks like a
// call (outcome, duration, Play) in both places and a reply is written the same
// way. Loads /comms/thread/ + /comms/calls/ for the target it is given.
//
//   <ConversationThread
//     target={{ party_universal_id, lead_id, customer_id, thread_id }}
//     contact={{ name, phone, email }}
//     reloadKey={n}            // bump to reload from outside
//     onSent={() => …}         // after a successful send
//   />
//
// AI drafting is EXPLICIT-ONLY (button click); nothing drafts on open.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../../api/client';
import {
  fetchThreadByTarget,
  fetchCallLogs,
  sendCommunication,
  fetchSmartReply,
  fetchSendingIdentity,
  triageCall,
} from '../../../api/communications';
import WrapUpModal from '../Calls/WrapUpModal';
import { chLabel, fmtDay, MessageItem, isGhostCall } from './InboxItems';
import './inbox.css';

const MAX_MEDIA = 5;

export default function ConversationThread({ target, contact = {}, reloadKey = 0, onSent, className = '' }) {
  const [messages, setMessages] = useState([]);
  const [callsBySid, setCallsBySid] = useState({});
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState('');
  const [subject, setSubject] = useState('');
  const [sendChannel, setSendChannel] = useState('sms');
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [wrapUpFor, setWrapUpFor] = useState(null);
  const [showGhosts, setShowGhosts] = useState(false);
  const [media, setMedia] = useState([]); // [{ name, url }]
  const [uploading, setUploading] = useState(false);
  const [tick, setTick] = useState(0);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const channelPicked = useRef(false);

  const hasTarget = Boolean(target && (target.party_universal_id || target.lead_id || target.customer_id || target.thread_id));
  const targetKey = JSON.stringify(target || {});

  useEffect(() => {
    fetchSendingIdentity().then(setIdentity).catch(() => {});
  }, []);

  useEffect(() => {
    if (!hasTarget) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    setFailed(false);
    (async () => {
      try {
        const t = JSON.parse(targetKey);
        const [thread, calls] = await Promise.all([
          fetchThreadByTarget(t),
          fetchCallLogs({ party_universal_id: t.party_universal_id, lead_id: t.lead_id, customer_id: t.customer_id }).catch(() => []),
        ]);
        if (!alive) return;
        const list = Array.isArray(thread) ? thread : (thread?.messages || []);
        setMessages(list);
        const bySid = {};
        (Array.isArray(calls) ? calls : []).forEach((c) => { if (c?.call_sid) bySid[c.call_sid] = c; });
        setCallsBySid(bySid);
        // Reply by however they last reached out — never by a channel they can't receive.
        if (!channelPicked.current) {
          const lastInbound = [...list].reverse().find((m) => m.direction === 'inbound');
          let ch = chLabel(lastInbound?.channel || 'sms');
          if (ch === 'call' || ch === 'voicemail' || ch === 'mms') ch = 'sms';
          if (ch === 'sms' && !contact.phone && contact.email) ch = 'email';
          if (ch === 'email' && !contact.email && contact.phone) ch = 'sms';
          setSendChannel(ch);
        }
      } catch {
        if (alive) setFailed(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, hasTarget, reloadKey, tick]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, loading]);

  const reload = () => setTick((n) => n + 1);

  // Replying by email continues the mail thread: subject from their last email.
  useEffect(() => {
    if (sendChannel !== 'email' || subject) return;
    const lastEmail = [...messages].reverse().find((x) => chLabel(x.channel) === 'email' && x.subject);
    if (lastEmail?.subject) setSubject(`Re: ${String(lastEmail.subject).replace(/^(\s*re:\s*)+/i, '')}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendChannel, messages]);

  const replyTargetId = useMemo(() => {
    const m = [...messages].reverse().find((x) => x.direction === 'inbound' && chLabel(x.channel) === 'email');
    return m?.id || null;
  }, [messages]);

  // Day separators, with C's ghost call rows folded away unless asked for.
  const { grouped, ghosts } = useMemo(() => {
    const voicemailSids = new Set(
      messages.filter((m) => chLabel(m.channel) === 'voicemail' && m.provider_message_id).map((m) => m.provider_message_id),
    );
    const sorted = [...messages].sort((a, b) => new Date(a.timestamp || a.created_at || 0) - new Date(b.timestamp || b.created_at || 0));
    let ghostCount = 0;
    const kept = sorted.filter((m) => {
      const call = callsBySid[m.provider_message_id];
      if (!isGhostCall(m, call)) return true;
      // The voicemail row already tells the story of this call; the ghost adds nothing.
      if (voicemailSids.has(m.provider_message_id)) return false;
      ghostCount += 1;
      return showGhosts;
    });
    const out = [];
    let lastDay = null;
    kept.forEach((m) => {
      const day = fmtDay(m.timestamp || m.created_at);
      if (day !== lastDay) { out.push({ _day: day, key: `day-${day}-${out.length}` }); lastDay = day; }
      out.push(m);
    });
    return { grouped: out, ghosts: ghostCount };
  }, [messages, callsBySid, showGhosts]);

  const triage = async (sid, payload) => {
    try {
      const res = await triageCall(sid, payload);
      setCallsBySid((m) => ({ ...m, [sid]: { ...m[sid], handled_at: res.handled_at, handled_by_name: res.handled_by_name, followup_task_id: res.followup_task_id } }));
      if (payload.create_followup && res.task) toast.success(`Task created: ${res.task.title}`);
      else if ('handled' in payload) toast.success(payload.handled ? 'Marked handled' : 'Reopened');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the call');
    }
  };

  const draftWithAi = async () => {
    if (!hasTarget) return;
    setDrafting(true);
    try {
      const res = await fetchSmartReply(JSON.parse(targetKey));
      const text = res?.reply || '';
      if (!text) { toast.info('No draft came back — write it by hand'); return; }
      setDraft(text);
      toast.success('Draft ready — edit and send');
    } catch {
      toast.error('Draft unavailable');
    } finally {
      setDrafting(false);
    }
  };

  const attach = async (file) => {
    if (!file) return;
    if (media.length >= MAX_MEDIA) { toast.error(`Up to ${MAX_MEDIA} attachments per message`); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const t = JSON.parse(targetKey);
      if (t.customer_id) form.append('customer', t.customer_id);
      else if (t.lead_id) form.append('lead', t.lead_id);
      const { data } = await api.post('/storage/files/upload/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = data?.url || data?.file_url || data?.file;
      if (!url) throw new Error('no url');
      setMedia((m) => [...m, { name: file.name, url }]);
    } catch {
      toast.error('Could not attach that file');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const send = async () => {
    if ((!draft.trim() && !media.length) || !hasTarget) return;
    const t = JSON.parse(targetKey);
    setSending(true);
    try {
      if (sendChannel === 'email') {
        if (!contact.email) { toast.error('No email address for this person'); return; }
        if (!t.lead_id && !t.customer_id) { toast.error('Email needs a linked customer or lead'); return; }
        await api.post('/comms/email/send/', {
          to_email: contact.email,
          subject: subject || '(no subject)',
          body_html: draft.replace(/\n/g, '<br>'),
          lead_id: t.lead_id || undefined,
          customer_id: t.customer_id || undefined,
          reply_to_log_id: replyTargetId || undefined,
          attachments: media.length ? media.map((m) => m.url) : undefined,
        });
      } else {
        await sendCommunication({
          channel: media.length ? 'mms' : 'sms',
          body: draft,
          media_urls: media.length ? media.map((m) => m.url) : undefined,
          lead_id: t.lead_id || undefined,
          customer_id: t.customer_id || undefined,
          party_universal_id: t.party_universal_id || undefined,
          to_phone: contact.phone || undefined,
        });
      }
      setDraft('');
      setSubject('');
      setMedia([]);
      toast.success(sendChannel === 'email' ? 'Email sent' : 'Message sent');
      reload();
      onSent?.();
    } catch (e) {
      const d = e?.response?.data;
      toast.error(d?.error || d?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  if (!hasTarget) {
    return <div className={`ubx-thread-only ${className}`}><div className="ubx-empty">No conversation yet — this person has no phone, email or linked record to reach them by.</div></div>;
  }

  return (
    <div className={`ubx-thread-only ${className}`}>
      {wrapUpFor ? (
        <WrapUpModal call={wrapUpFor} onClose={() => setWrapUpFor(null)} onSaved={() => { setWrapUpFor(null); reload(); }} />
      ) : null}

      <div className="ubx-msgs">
        {loading ? <div className="ubx-empty">Loading conversation…</div> : null}
        {!loading && failed ? <div className="ubx-empty">Couldn’t load this conversation. <button type="button" className="ubx-quote-toggle" onClick={reload}>Try again</button></div> : null}
        {!loading && !failed && grouped.length === 0 ? <div className="ubx-empty">No messages yet. Write the first one below.</div> : null}
        {grouped.map((m, i) =>
          m._day ? (
            <div className="ubx-day" key={m.key}>{m._day}</div>
          ) : (
            <MessageItem key={m.id || `${m.timestamp}-${i}`} msg={m} call={callsBySid[m.provider_message_id]} onTriage={triage} onWrapUp={setWrapUpFor} />
          ))}
        {ghosts ? (
          <button type="button" className="ubx-quote-toggle ubx-ghosts" onClick={() => setShowGhosts((v) => !v)}>
            {showGhosts ? `Hide ${ghosts} system row${ghosts === 1 ? '' : 's'}` : `Show ${ghosts} system row${ghosts === 1 ? '' : 's'}`}
          </button>
        ) : null}
        <div ref={endRef} />
      </div>

      <div className="ubx-composer">
        <div className="ubx-composer-top">
          <span className="ubx-hint">Reply by</span>
          {['sms', 'email'].map((c) => (
            <button
              key={c}
              type="button"
              className={`ubx-chip ${sendChannel === c ? 'on' : ''}`}
              disabled={c === 'email' ? !contact.email : !contact.phone}
              title={c === 'email' && !contact.email ? 'No email address for this person' : c === 'sms' && !contact.phone ? 'No phone number for this person' : undefined}
              onClick={() => { channelPicked.current = true; setSendChannel(c); }}
            >
              {c === 'sms' ? 'SMS' : 'Email'}
            </button>
          ))}
          <button type="button" className="ubx-btn sm" onClick={draftWithAi} disabled={drafting}>
            {drafting ? 'Drafting…' : '✨ Draft with AI'}
          </button>
          <button type="button" className="ubx-btn sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Attach'}
          </button>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={(e) => attach(e.target.files?.[0])} />
        </div>
        {sendChannel === 'email' ? (
          <>
            {identity?.from_address ? (
              <div className="ubx-identity">
                Sends as {identity.from_address}
                {identity.reply_to ? ` · replies go to ${identity.reply_to}` : ''}
                {replyTargetId ? ' · threads with their last email' : ''}
              </div>
            ) : null}
            <input className="ubx-subject" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </>
        ) : null}
        {media.length ? (
          <div className="ubx-media">
            {media.map((m) => (
              <span key={m.url} className="ubx-chip on">
                {m.name}
                <button type="button" aria-label={`Remove ${m.name}`} onClick={() => setMedia((x) => x.filter((y) => y.url !== m.url))}>×</button>
              </span>
            ))}
          </div>
        ) : null}
        <textarea
          placeholder={sendChannel === 'email' ? 'Write your email…' : 'Write a message…'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && sendChannel === 'sms') { e.preventDefault(); send(); }
          }}
        />
        <div className="ubx-composer-actions">
          <span className="ubx-hint">AI drafts land here for your edit — nothing sends on its own.</span>
          <button type="button" className="ubx-btn primary" onClick={send} disabled={sending || (!draft.trim() && !media.length)}>
            {sending ? 'Sending…' : `Send ${sendChannel === 'email' ? 'email' : media.length ? 'MMS' : 'SMS'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
